import { Button, Divider, Form, Input, Modal, Popconfirm, Select, Tooltip, message } from 'antd';
import {
    ContactsOutlined, CopyOutlined, DeleteOutlined, ExportOutlined, IdcardOutlined, KeyOutlined,
    MailOutlined, PhoneOutlined, PlusOutlined, TagsOutlined, UserOutlined,
} from '@ant-design/icons';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { serverFetch, fileUrl } from '@server';
import { showError, fmtDateTime } from '@extensions/utils';
import { formatPhone } from '@extensions/contact-mask';
import { useDicts, channelVidName } from '@extensions/dicts';
import TagSelect from '@controls/tag-select/tag-select';

// Ленивый импорт: contragent.js через dialog.js уже импортирует dialog-main.js (циклическая зависимость)
const Contragent = lazy(() => import('../contragents/contragent'));

const ROLE_MANAGER = 10;
const CHANNEL_SITE = 201;

const Prop = ({ icon, label, children }) => (
    <div className="task-prop">
        <span className="task-prop-label">{icon}{label}</span>
        <div className="task-prop-value">{children}</div>
    </div>
);

// Вкладка «Карточка» диалога: свойства, метки, действия
const DialogMain = ({ dialog, onSaved, onOpenContragent, onCreatedTask, onArchived, onClose }) => {
    const user = useSelector((s) => s.user);
    const { dicts } = useDicts();
    const [form] = Form.useForm();
    const [contragentOptions, setContragentOptions] = useState([]);
    const [contragentSearch, setContragentSearch] = useState('');
    const [creatingContragent, setCreatingContragent] = useState(null);
    const [taskModal, setTaskModal] = useState(false);
    const [taskForm] = Form.useForm();
    const readonly = user?.role > ROLE_MANAGER;
    const isManager = user?.role <= ROLE_MANAGER;

    useEffect(() => {
        form.setFieldsValue({
            name: dialog.name,
            userId: dialog.userId ?? undefined,
            contragentId: dialog.contragentId ?? undefined,
            enteredName: dialog.enteredName,
            enteredPhone: dialog.enteredPhone,
            enteredEmail: dialog.enteredEmail,
            note: dialog.note,
            tags: dialog.tags ?? [],
        });
        if (dialog.contragentId) {
            setContragentOptions((prev) => {
                if (prev.some((o) => o.value === dialog.contragentId)) return prev;
                return [...prev, { value: dialog.contragentId, label: dialog.contragentName || `#${dialog.contragentId}` }];
            });
        }
    }, [dialog, form]);

    const onContragentSearch = useCallback((text) => {
        setContragentSearch(text);
        if (!text || text.length < 2) return;
        serverFetch(`contragent/search?name=${encodeURIComponent(text)}&limit=20`)
            .then((list) => setContragentOptions(list.map((c) => ({ value: c.id, label: c.name }))))
            .catch(showError);
    }, []);

    const openCreateContragent = () => setCreatingContragent({
        name: contragentSearch || dialog.enteredName || dialog.name || '',
        phone: dialog.enteredPhone || '',
        email: dialog.enteredEmail || '',
    });

    const onContragentCreated = (c) => {
        setContragentOptions((prev) => (prev.some((o) => o.value === c.id)
            ? prev
            : [...prev, { value: c.id, label: c.name }]));
        form.setFieldsValue({ contragentId: c.id });
        setCreatingContragent(null);
    };

    const onSave = () => {
        form.validateFields().then((values) => {
            serverFetch(`dialog/${dialog.id}`, {
                method: 'PUT',
                bodyData: {
                    ...dialog,
                    name: values.name ?? '',
                    userId: values.userId ?? null,
                    contragentId: values.contragentId ?? null,
                    note: values.note ?? '',
                    enteredName: values.enteredName ?? '',
                    enteredPhone: values.enteredPhone ?? '',
                    enteredEmail: values.enteredEmail ?? '',
                    tags: values.tags ?? [],
                },
            })
                .then((saved) => {
                    onSaved?.(saved);
                    onClose?.();
                })
                .catch(showError);
        });
    };

    const onCreateContragent = () => {
        serverFetch(`dialog/${dialog.id}/contragent`, { method: 'POST' })
            .then((c) => {
                onSaved?.({ ...dialog, contragentId: c.id, contragentName: c.name });
            })
            .catch(showError);
    };

    const onRequestUserInfo = () => {
        serverFetch(`dialog/${dialog.id}/request-user-info`, { method: 'POST' })
            .catch(showError);
    };

    const onSetDeleted = (deleted) => {
        serverFetch(`dialog/${dialog.id}/${deleted ? 'delete' : 'restore'}`, { method: 'PUT' })
            .then(() => onArchived?.())
            .catch(showError);
    };

    const onCreateTask = () => {
        taskForm.validateFields().then((values) => {
            serverFetch(`dialog/${dialog.id}/task`, {
                method: 'POST',
                bodyData: { name: values.name, comment: values.comment },
            })
                .then((task) => {
                    setTaskModal(false);
                    onCreatedTask?.(task);
                })
                .catch(showError);
        });
    };

    const openTaskModal = () => {
        const lastIncoming = dialog.lastMessageDirection === 1 ? dialog.lastMessageText : '';
        taskForm.setFieldsValue({
            name: `Диалог: ${dialog.name || dialog.enteredName || dialog.contragentName || `#${dialog.id}`}`,
            comment: lastIncoming || '',
        });
        setTaskModal(true);
    };

    const userOpts = dicts.users.map((u) => ({ value: u.id, label: u.name }));

    return (
        <div className="task-section ca-tab">
            <Form form={form} layout="vertical" onFinish={onSave} disabled={readonly}>
                <div className="task-props-grid">
                    <Prop icon={<IdcardOutlined />} label="Имя">
                        <Form.Item name="name" noStyle>
                            <Input variant="borderless" placeholder="—" />
                        </Form.Item>
                    </Prop>
                    <Prop icon={<TagsOutlined />} label="Канал">
                        <span className="dlg-prop-readonly">
                            {dialog.channelIconFileId ? (
                                <img className="channel-icon" src={fileUrl(dialog.channelIconFileId, true)} alt="" />
                            ) : null}
                            {channelVidName(dialog.channelVid)}{dialog.channelName ? ` · ${dialog.channelName}` : ''}
                        </span>
                    </Prop>
                    {dialog.externalId ? (
                        <Prop icon={<KeyOutlined />} label="Ключ">
                            <div className="task-inline">
                                <span className="dlg-prop-readonly" style={{ flex: 1, minWidth: 0 }}>{dialog.externalId}</span>
                                <Tooltip title="Копировать">
                                    <Button
                                        type="text" size="small" icon={<CopyOutlined />}
                                        onClick={() => {
                                            navigator.clipboard?.writeText(dialog.externalId);
                                            message.success('Скопировано');
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        </Prop>
                    ) : null}
                    {dialog.line ? (
                        <Prop icon={<PhoneOutlined />} label="Линия">
                            <span className="dlg-prop-readonly">{formatPhone(dialog.line)}</span>
                        </Prop>
                    ) : null}
                    <Prop icon={<UserOutlined />} label="Менеджер">
                        <Form.Item name="userId" noStyle>
                            <Select allowClear showSearch optionFilterProp="label" variant="borderless" placeholder="—" options={userOpts} />
                        </Form.Item>
                    </Prop>
                    <Prop icon={<ContactsOutlined />} label="Контрагент">
                        <div className="task-inline">
                            <Form.Item name="contragentId" noStyle>
                                <Select
                                    allowClear showSearch variant="borderless" filterOption={false}
                                    onSearch={onContragentSearch} options={contragentOptions}
                                    placeholder="Начните вводить" style={{ flex: 1, minWidth: 0 }}
                                    popupRender={readonly ? undefined : (menu) => (
                                        <>
                                            {menu}
                                            <Divider style={{ margin: '4px 0' }} />
                                            <Button
                                                type="text"
                                                icon={<PlusOutlined />}
                                                style={{ width: '100%', textAlign: 'left' }}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={openCreateContragent}
                                            >
                                                Создать контрагента{contragentSearch ? ` «${contragentSearch}»` : ''}
                                            </Button>
                                        </>
                                    )}
                                />
                            </Form.Item>
                            {!readonly && (
                                <Tooltip title="Создать контрагента">
                                    <Button type="text" size="small" icon={<PlusOutlined />} onClick={openCreateContragent} />
                                </Tooltip>
                            )}
                            {onOpenContragent && dialog.contragentId && (
                                <Tooltip title="Открыть контрагента">
                                    <Button type="text" size="small" icon={<ExportOutlined />}
                                        onClick={() => onOpenContragent(dialog.contragentId)} />
                                </Tooltip>
                            )}
                        </div>
                    </Prop>
                    <Prop icon={<IdcardOutlined />} label="Имя (введённое)">
                        <Form.Item name="enteredName" noStyle><Input variant="borderless" placeholder="—" /></Form.Item>
                    </Prop>
                    <Prop icon={<PhoneOutlined />} label="Телефон">
                        <Form.Item name="enteredPhone" noStyle><Input variant="borderless" placeholder="—" /></Form.Item>
                    </Prop>
                    <Prop icon={<MailOutlined />} label="Email">
                        <Form.Item name="enteredEmail" noStyle><Input variant="borderless" placeholder="—" /></Form.Item>
                    </Prop>
                    <Prop icon={<TagsOutlined />} label="Метки">
                        <Form.Item name="tags" noStyle>
                            <TagSelect tags={dicts.tags} vid={6} variant="borderless" placeholder="—" />
                        </Form.Item>
                    </Prop>
                </div>

                <div className="task-section" style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <div className="task-section-title">Заметка</div>
                    <Form.Item name="note">
                        <Input.TextArea variant="filled" autoSize={{ minRows: 2, maxRows: 8 }} />
                    </Form.Item>
                </div>

                {!readonly && (
                    <div className="task-actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <Button type="primary" htmlType="submit" className="dlg-ok">ОК</Button>
                        {!dialog.contragentId && isManager && (
                            <Button onClick={onCreateContragent}>Создать из контакта</Button>
                        )}
                        <Button onClick={openTaskModal}>Создать задачу</Button>
                        {dialog.channelVid === CHANNEL_SITE && isManager && (
                            <Button onClick={onRequestUserInfo}>Запросить имя</Button>
                        )}
                        {isManager && (dialog.deleted ? (
                            <Button onClick={() => onSetDeleted(false)}>Восстановить</Button>
                        ) : (
                            <Popconfirm title="Отправить диалог в архив?" okText="Да" cancelText="Нет" onConfirm={() => onSetDeleted(true)}>
                                <Button danger>В архив</Button>
                            </Popconfirm>
                        ))}
                        {user?.isSuperadmin && (
                            <Popconfirm
                                title="Удалить диалог безвозвратно вместе с сообщениями и файлами?"
                                okText="Удалить"
                                cancelText="Отмена"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => {
                                    serverFetch(`dialog/${dialog.id}`, { method: 'DELETE' })
                                        .then(() => onArchived?.())
                                        .catch(showError);
                                }}
                            >
                                <Button danger type="text" icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }}>Удалить</Button>
                            </Popconfirm>
                        )}
                    </div>
                )}
                {dialog.lastUpdated && <div className="card-list-muted" style={{ marginTop: 8 }}>обновлён {fmtDateTime(dialog.lastUpdated)}</div>}
            </Form>

            <Modal
                title="Создать задачу из диалога"
                open={taskModal}
                onCancel={() => setTaskModal(false)}
                onOk={onCreateTask}
                okText="Создать"
                cancelText="Отмена"
                destroyOnHidden={false}
            >
                <Form form={taskForm} layout="vertical">
                    <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите наименование' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="comment" label="Комментарий">
                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} />
                    </Form.Item>
                </Form>
            </Modal>
            {creatingContragent && (
                <Suspense fallback={null}>
                    <Contragent
                        id={0}
                        initial={creatingContragent}
                        onClose={() => setCreatingContragent(null)}
                        onSaved={onContragentCreated}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default DialogMain;
